import { pickBilingual, type Locale } from '@lims/shared-types';

/**
 * Render a bilingual value for display.
 *
 * `pickBilingual` falls back to the other language rather than showing an
 * empty cell, which is right: a client whose name was only ever entered in
 * Arabic should still be readable on an English screen. It is a FALLBACK, not
 * a translation — nothing is ever translated automatically (CLAUDE.md
 * section 7).
 *
 * Returns null when neither language has a value, so the caller can say
 * "not recorded" rather than render a blank that looks like a rendering bug.
 */
export function displayName(
  value: { ar: string | null; en: string | null } | null | undefined,
  locale: Locale,
): string | null {
  if (!value) return null;
  return pickBilingual(value, locale);
}

/** The two halves of a bilingual field, as a form holds them. */
export interface BilingualDraft {
  ar: string;
  en: string;
}

export function toDraft(
  value: { ar: string | null; en: string | null } | null | undefined,
): BilingualDraft {
  return { ar: value?.ar ?? '', en: value?.en ?? '' };
}

/**
 * A bilingual field as the API expects it.
 *
 * An untouched half becomes `null`, never `''`. An empty string stored in the
 * database would look like a recorded fact — someone deciding this client has
 * no Arabic name — rather than a field nobody filled in
 * (CLAUDE.md section 8: an empty field is never zero).
 */
export function fromDraft(
  draft: BilingualDraft,
): { ar: string | null; en: string | null } {
  return {
    ar: draft.ar.trim() || null,
    en: draft.en.trim() || null,
  };
}

/** True when neither half has content. */
export function isDraftEmpty(draft: BilingualDraft): boolean {
  return !draft.ar.trim() && !draft.en.trim();
}

/** An optional single-language text field, on the same rule as above. */
export function optionalText(value: string): string | null {
  return value.trim() || null;
}
